<template>
    <div class="my-home-edit">
        <h2 class="my-home-edit__title --desktop-only">
            {{ property ? property.asString() : myResources.title }}
        </h2>
        <h2 class="my-home-edit__intermediate-title --not-desktop">
            {{ property ? property.asShortString() : myResources.title }}
        </h2>

        <error-handler :error="errorMessage" />
        <div class="flow__container-fluid">
            <div>
                <div class="my-home-edit__text-panel">
                    <p>
                        {{ myResources.introText }}
                    </p>
                </div>
                <hh-tab-group class="my-home-edit__tab-group"
                              @activate="prepareEdit"
                              :active="tab">
                    <template v-slot:tab="slotProps">
                        <span class="my-home-edit__tab--title">
                            {{ slotProps.tab.content.text }}
                        </span>
                        <segment-icon
                            :segment-key="slotProps.tab.content.key"
                            class="my-home-edit__tab--icon" />
                    </template>
                    <hh-tab-panel v-for="segment in segmentMaps"
                                  :key="segment.key"
                                  class="my-home-edit__tab-panel"
                                  :content="segment"
                                  :title="segment.text"
                                  :can-change="validateTab">
                        <component :is="segmentContentComponent(segment)"
                                   :segment="getSegmentData(segment.key)"
                                   editing
                                   v-show="!saving"
                                   @save="onSave" />
                        <loading-animation v-if="saving">
                            Saving&hellip;
                        </loading-animation>
                    </hh-tab-panel>
                </hh-tab-group>
            </div>
            <div class="my-home-edit__sidebar">
                <attributions-panel />
            </div>
        </div>
    </div>
</template>

<script>

import Vuex from 'vuex'

import PlanBuilderManager from '@/plan-builder-manager'
import ProgressManager from '@/progress-manager'
import PropertyManager from '@/property-manager'

import ResourcesHelper from '@/components/resources-helper'
import AnalyticsPage from './analytics-page'
import MyHomeDataErrorsDisplay from './my-home-data-errors-display'
import AttributionsPanel from '@/components/app-core/attributions-panel'
import ErrorHandler from '@/components/app-core/error-handler'
import InfoPanelButtons from '@/components/my-home/info-panel-buttons'
import LoadingAnimation from '@/components/app-core/loading-animation'
import SaveDialog from '@/components/app-core/modals/save-dialog'
import SegmentIcon from '@/components/my-home/segment-icon'
import SegmentInfoPanel from '@/components/my-home/segment-info-panel'

export default {
    mixins: [
        AnalyticsPage,
        MyHomeDataErrorsDisplay,
        ResourcesHelper,
    ],

    data() {
        return {
            /**
             * @type {boolean}
             */
            saving: false,

            /**
             * @type {string}
             */
            errorMessage: '',

            /**
             * @type {string}
             */
            tab: 'Basics',
        }
    },

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {?PropertyAsJson}
             */
            property: stateStore => stateStore.property,

            /**
             * @param {VuexState} stateStore
             * @returns {Array}
             */
            segments: stateStore => stateStore.myHome.segments,

            /**
             * @param {VuexState} stateStore
             * @returns {boolean}
             */
            hasEdits: stateStore => (stateStore.myHome.edits || []).filter(edit => edit.value != null)
                .map(edit => edit.value)
                .filter(value => value.initial !== value.edited)
                .length > 0,
        }),

        /**
         * @returns {MyHomeEditResources}
         */
        myResources() {
            return this.parseResources('views.myHomeEdit')
        },

        /**
         * @returns {SegmentKeyMap[]}
         */
        segmentMaps() {
            return this.$scheme.segmentKeymap
        },

        /**
         * @returns {number}
         */
        propertyId() {
            return parseInt(this.$route.query.id !== null ? this.$route.query.id : 0)
        },
    },

    async created() {
        await PropertyManager.loadHomeDataIfNeededOrForcedAsync(this.propertyId)
        this.$logger.logObject(this.segments, 'Home Data loaded', 'My Home Edit')
        this.$logger.log('Trying to load default segment data', 'My Home Edit')
        PropertyManager.Edits.initialise(this.getSegmentData('basics'))
    },

    beforeMount() {
        if (!this.property || !this.property.isServedByScheme())
            this.$router.push(ProgressManager.getPrevious(this.$route.name))

    },

    methods: {
        /**
         * @param {string} keymapName The $key name of the segment to retrieve.
         * @returns {Segment}
         */
        getSegmentData(keymapName) {
            this.$logger.logObject(this.segments, `Getting segment data for ${ keymapName }`, 'My Home Edit')
            return this.segments.find(seg => seg.$key === keymapName)
        },

        /**
         * Get the component used to display the segment.
         *
         * @params {SegmentKeyMap} keymap
         * @returns {VueComponent}
         */
        segmentContentComponent(keymap) {
            return keymap && keymap.component ? keymap.component : SegmentInfoPanel
        },

        /**
         * Prepare segment data for editing when a new tab is opened.
         *
         * @param {VueComponent} tab The which has opened.
         */
        prepareEdit(tab) {
            this.$logger.log(`Preparing segment data for tab key ${ tab.content.key }`, 'My Home Edit')
            PropertyManager.Edits.initialise(this.getSegmentData(tab.content.key))
        },

        /**
         * Validate a tab before it opens. If the current panel has edits open the SaveDialog modal and
         * prevent the tab from opening.
         *
         * @param {VueComponent} tab The tab which is attempting to open.
         * @returns {boolean}
         */
        validateTab(tab) {
            if (this.hasEdits) {
                this.$logger.log(`Validating data for tab key ${ tab ? tab.content.key : 'UNKNOWN' }`, 'My Home Edit')
                this.$eventbus.$emit(this.$appevents.showModal, SaveDialog, {
                    onClose: async() => {
                        const segmentData = this.getSegmentData(tab.content.key)

                        await this.onSave(segmentData, false)

                        this.tab = tab.title
                    },
                })

                return false
            }

            return true
        },

        /**
         * Save the provided segment data.
         *
         * @param {Segment} segment The Segment object to save.
         * @param {boolean} exit Exit this page after saving if true, otherwise remain on this page.
         * @param {Function} callback Function to call when saving is complete.
         */
        async onSave(segment, exit, callback = () => {}) {
            this.saving = true

            try {
                await PropertyManager.updateSegmentFieldsAsync(this.property.id, segment.$key)
                await PlanBuilderManager.clearAsync()

                this.trackEvent('home_info', 'editing_home_info_saved')

                if (exit)
                    this.$router.push(ProgressManager.getNext(this.$route.name, { id: this.property.id }))
                else
                    PropertyManager.Edits.initialise(this.getSegmentData(segment.$key))

            } catch (err) {
                this.errorMessage = `Unable to save changes: ${ err ? err.message : 'An error occurred' }`
            }

            this.saving = false
            callback()
        },
    },

    components: {
        AttributionsPanel,
        ErrorHandler,
        InfoPanelButtons,
        LoadingAnimation,
        SegmentIcon,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_my-home.scss

.my-home-edit {
    padding-bottom: calc($cta-tile-breakout-double-height + $footer-height-base + 20px);

    @include element('tab') {
        @include modifier('title') {
            display: none;

            @include bp-min-mid {
                display: block;
            }
        }

        @include modifier('icon') {
            display: inline-block;

            @include bp-min-mid {
                display: none;
            }
        }
    }

    @include element('title') {
        padding-left: 10px;
        padding-right: 10px;
    }

    @include element('text-panel') {
        margin-bottom: 2rem;
    }

    @include element('sidebar') {
        display: none;

        @include bp-min-small {
            display: block;
            padding-top: 1rem;
        }

        @include bp-min-wide {
            padding-top: 0;
            padding-left: rem(50px);
        }
    }

    .segment-icon__icon-wrapper {
        margin-top: 6px;
    }
}

</style>
